import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiAward, FiTrendingUp, FiPieChart, FiCheckCircle, FiTarget } = FiIcons;

const SummarySection = ({ assessmentData, ratings }) => {
  // Calculate average for each section
  const sectionSummaries = assessmentData.map(section => {
    const sectionRatings = section.items
      .map(item => ratings[`${section.id}-${item.id}`])
      .filter(r => r !== undefined);
    
    const average = sectionRatings.length > 0 
      ? (sectionRatings.reduce((a, b) => a + b, 0) / sectionRatings.length).toFixed(1)
      : "N/A";

    return {
      title: section.title,
      average,
      count: sectionRatings.length,
      total: section.items.length
    };
  });

  // Flatten all items to find top strengths and growth areas
  const allRatedItems = assessmentData.flatMap(section => 
    section.items.map(item => ({
      id: `${section.id}-${item.id}`,
      title: item.title,
      sectionTitle: section.title,
      rating: ratings[`${section.id}-${item.id}`] || 0
    }))
  );

  const topStrengths = [...allRatedItems]
    .sort((a, b) => b.rating - a.rating)
    .slice(0, 3);

  const topGrowthAreas = [...allRatedItems]
    .sort((a, b) => a.rating - b.rating)
    .slice(0, 3);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2 flex items-center gap-2">
          <SafeIcon icon={FiPieChart} className="text-brand-primary" />
          Assessment Summary
        </h2>
        <p className="text-gray-600">Automated analysis based on your self-ratings.</p>
      </div>

      {/* Section Averages */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {sectionSummaries.map((summary, idx) => (
          <div key={idx} className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex justify-between items-center">
            <div>
              <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-1">{summary.title}</h4>
              <p className="text-xs text-gray-400">{summary.count} of {summary.total} items rated</p>
            </div>
            <div className={`text-2xl font-bold px-3 py-1 rounded-lg ${
              summary.average >= 4 ? 'text-green-600 bg-green-50' : 
              summary.average >= 3 ? 'text-blue-600 bg-blue-50' : 
              'text-orange-600 bg-orange-50'
            }`}>
              {summary.average}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top 3 Strengths */}
        <div className="bg-green-50 rounded-2xl p-6 border border-green-100">
          <h3 className="text-lg font-bold text-green-800 mb-4 flex items-center gap-2">
            <SafeIcon icon={FiAward} />
            Top 3 Strengths
          </h3>
          <div className="space-y-3">
            {topStrengths.map((item, idx) => (
              <div key={idx} className="bg-white p-4 rounded-lg shadow-sm border border-green-200 flex items-center justify-between">
                <div>
                  <p className="font-semibold text-gray-800 text-sm">{item.title}</p>
                  <p className="text-xs text-gray-500">{item.sectionTitle}</p>
                </div>
                <div className="bg-green-600 text-white text-xs font-bold w-8 h-8 flex items-center justify-center rounded-full shrink-0">
                  {item.rating}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top 3 Growth Areas */}
        <div className="bg-orange-50 rounded-2xl p-6 border border-orange-100">
          <h3 className="text-lg font-bold text-orange-800 mb-4 flex items-center gap-2">
            <SafeIcon icon={FiTrendingUp} />
            Top 3 Growth Areas
          </h3>
          <div className="space-y-3">
            {topGrowthAreas.map((item, idx) => (
              <div key={idx} className="bg-white p-4 rounded-lg shadow-sm border border-orange-200 flex items-center justify-between">
                <div>
                  <p className="font-semibold text-gray-800 text-sm">{item.title}</p>
                  <p className="text-xs text-gray-500">{item.sectionTitle}</p>
                </div>
                <div className="bg-orange-600 text-white text-xs font-bold w-8 h-8 flex items-center justify-center rounded-full shrink-0">
                  {item.rating}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-brand-primary rounded-xl p-6 text-white text-center">
        <SafeIcon icon={FiCheckCircle} className="text-4xl mx-auto mb-3" />
        <h3 className="text-xl font-bold mb-2">Ready to Finalize</h3>
        <p className="text-brand-light mb-4 text-sm">Review your summary above before exporting the final assessment report.</p>
      </div>
    </motion.div>
  );
};

export default SummarySection;